




import { AsyncComponent, Component, PropType, VNode, VNodeData } from 'vue';
import { VNodeChildren } from 'vue/types';

import { ContractPhaseEnum, ContractStateEnum, IContract } from '@/models';

import mixins from '@/utils/mixins';
import auth from '@/mixins/auth';

import VNodes from '@/components/computation/VNodes.vue';
import ContractExecuteStateButton from './ContractExecuteStateButton.vue';
import ContractTerminateStateButton from './ContractTerminateStateButton.vue';
import ContractStartRenewalStateButton from './ContractStartRenewalStateButton.vue';
import ContractRenewStateButton from './ContractRenewStateButton.vue';
import ContractUndoExecuteStateButton from './ContractUndoExecuteStateButton.vue';

type ViewType = 'list' | 'detail';
type ComponentTag =
  | string
  | Component<any, any, any, any>
  | AsyncComponent<any, any, any, any>
  | (() => Component);

export default mixins(auth).extend({
  name: 'ContractStateActions',

  components: {
    VNodes
  },

  props: {
    viewType: String as PropType<ViewType>,
    contract: {
      type: Object as PropType<IContract>
    }
  },

  methods: {
    genStateButton(
      node: ComponentTag,
      text: string,
      viewType: ViewType
    ): VNode {
      return this.$createElement(node, {
        attrs: { contract: this.contract },
        scopedSlots:
          viewType === 'list'
            ? {
                activator: ({ on, attrs }) =>
                  this.$createElement(
                    'VListItem',
                    {
                      on: on,
                      attrs: attrs
                    },
                    [this.$createElement('VListItemTitle', text)]
                  )
              }
            : undefined
      });
    },

    genContractTerminateStateButton(viewType: ViewType): VNode {
      return this.genStateButton(
        ContractTerminateStateButton,
        'Terminate Contract',
        viewType
      );
    },

    genContractExecuteStateButton(viewType: ViewType): VNode {
      return this.genStateButton(
        ContractExecuteStateButton,
        'Execute Contract',
        viewType
      );
    },

    genContractUndoExecuteStateButton(viewType: ViewType): VNode {
      return this.genStateButton(
        ContractUndoExecuteStateButton,
        'Undo Contract',
        viewType
      );
    },

    genContractStartRenewalStateButton(viewType: ViewType): VNode {
      return this.genStateButton(
        ContractStartRenewalStateButton,
        'Start Renewal Process',
        viewType
      );
    },

    genContractRenewStateButton(viewType: ViewType): VNode {
      return this.genStateButton(
        ContractRenewStateButton,
        'Manage Renewal',
        viewType
      );
    },

    genContractEditButton(viewType: ViewType): VNode {
      const isListView = viewType === 'list';
      const text = 'Edit Contract';
      return this.$createElement(
        isListView ? 'VListItem' : 'VBtn',
        {
          on: {
            click: () => this.$emit('contract-edit-pressed', this.contract)
          }
        },
        isListView ? [this.$createElement('VListItemTitle', text)] : text
      );
    },

    genActionsList(): ((viewType: ViewType) => VNode)[] {
      const children: ((viewType: ViewType) => VNode)[] = [];

      if (this.contract.state == ContractStateEnum.Draft) {
        // Execute
        if (this.userHasPermissions(['contracts.execute_contract'])) {
          children.push(this.genContractExecuteStateButton);
        }
        // Edit
        if (this.userHasPermissions(['contracts.change_contract'])) {
          children.push(this.genContractEditButton);
        }

        // Terminate
        if (
          this.userHasPermissions(['contracts.renew_contract']) &&
          !this.contract.isExpired()
        ) {
          children.push(this.genContractTerminateStateButton);
        }
      } else if (this.contract.state == ContractStateEnum.Executed) {
        if (
          this.contract.renewal_required &&
          this.userHasPermissions(['contracts.renew_contract'])
        ) {
          const canStartRenewalProcess = Boolean(
            this.contract.phase == ContractPhaseEnum.Active &&
              !this.contract.hasRenewalProcessStarted()
          );

          if (canStartRenewalProcess) {
            children.push(this.genContractStartRenewalStateButton);
          } else if (!this.contract.isContractRenewed()) {
            children.push(this.genContractRenewStateButton);
          }
        }

        if (
          this.userHasPermissions(['contracts.change_contract']) &&
          !this.contract.hasRenewalProcessStarted()
        ) {
          children.push(this.genContractUndoExecuteStateButton);
        }

        if (
          this.userHasPermissions(['contracts.renew_contract']) &&
          !this.contract.isExpired()
        ) {
          children.push(this.genContractTerminateStateButton);
        }
      }

      return children;
    },

    genStateViewActions(): VNode {
      const proxyChildren = this.genActionsList();

      let children: VNodeChildren = [];

      if (this.viewType === 'list') {
        children = proxyChildren.map(genNode => genNode('list'));
      } else {
        const genPrimaryActionButton = proxyChildren.shift();
        if (genPrimaryActionButton) {
          children.push(genPrimaryActionButton('detail'));
        }

        if (proxyChildren.length > 0) {
          const genMoreOptionsListMenu = this.$createElement(
            'VMenu',
            {
              attrs: {
                offsetY: true
              },

              scopedSlots: {
                activator: ({ on, attrs }) =>
                  this.$createElement(
                    'VBtn',
                    {
                      on: on,
                      attrs: { ...attrs, rounded: true },
                      class: 'text-capitalize'
                    },
                    [
                      'More Options',
                      this.$createElement('VIcon', 'mdi-menu-down')
                    ]
                  )
              }
            },
            [
              this.$createElement(
                'VList',
                {
                  attrs: {
                    dense: true
                  }
                },
                proxyChildren.map(genNode => genNode('list'))
              )
            ]
          );

          children.push(
            this.$createElement(
              'span',
              {
                class: 'ml-4'
              },
              [genMoreOptionsListMenu]
            )
          );
        }
      }

      return this.$createElement('div', children);
    }
  }
});
